import { useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Lock } from "lucide-react";

// Project-standard non-searchable single-select dropdown. Matches the SearchableSelect popover-
// without-Portal pattern so it works inside Modal / SideDrawer (Dialog onInteractOutside would
// otherwise eat clicks on portaled menu items). When used on an inline page where the parent
// forms a stacking context (e.g. inside a Card), set usePortal so the menu escapes to body.
//
// Per CLAUDE.md: prefer Radix Themes Select.Root for non-Dialog cases; reach for this when you
// need disabled-with-reason items, when the option list needs subtitle notes, or when the
// dropdown lives inside a Dialog.
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  note?: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  usePortal?: boolean;
}

export default function Select({
  value,
  onChange,
  options,
  placeholder = "Select…",
  usePortal = false,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const display = options.find((o) => o.value === value)?.label ?? placeholder;

  const content = (
    <Popover.Content
      align="start"
      sideOffset={4}
      style={{
        minWidth: "var(--radix-popover-trigger-width)",
        padding: 4,
        zIndex: 12000,
        // Fallback chain: Theme variables aren't defined when Portal escapes outside the Theme
        // provider; gray-1 → white keeps the menu solid.
        backgroundColor: "var(--color-panel-solid, var(--gray-1, white))",
        border: "1px solid var(--gray-a5)",
        borderRadius: "var(--radius-3)",
        boxShadow: "var(--shadow-4)",
      }}
    >
      {options.map((opt) => (
        <div
          key={opt.value}
          onClick={() => {
            if (opt.disabled) return;
            onChange(opt.value);
            setOpen(false);
          }}
          style={{
            padding: "6px 8px",
            borderRadius: 4,
            cursor: opt.disabled ? "not-allowed" : "pointer",
            opacity: opt.disabled ? 0.55 : 1,
            fontSize: 13,
            fontWeight: opt.value === value ? 600 : 400,
            backgroundColor: opt.value === value ? "var(--accent-3)" : undefined,
            color: "var(--gray-12)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
          onMouseEnter={(e) => {
            if (!opt.disabled && opt.value !== value)
              e.currentTarget.style.backgroundColor = "var(--gray-4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor =
              opt.value === value ? "var(--accent-3)" : "";
          }}
        >
          <Flex align="center" gap="2">
            {opt.disabled && <Lock size={11} />}
            <span>{opt.label}</span>
          </Flex>
          {opt.note && (
            <Text size="1" color="gray" style={{ marginLeft: opt.disabled ? 17 : 0 }}>
              {opt.note}
            </Text>
          )}
        </div>
      ))}
    </Popover.Content>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="surface"
          style={{ width: "100%", justifyContent: "space-between", fontWeight: "normal" }}
        >
          <Text size="2" truncate>{display}</Text>
          <ChevronDown size={14} />
        </Button>
      </Popover.Trigger>
      {usePortal ? <Popover.Portal>{content}</Popover.Portal> : content}
    </Popover.Root>
  );
}
